import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import Footer from "@/components/layout/Footer";
import WhatsAppButton from "./components/layout/WhatsAppButton";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { PropertyErrorFallback } from "./components/properties/PropertyErrorFallback";
import CustomCursor from "./components/common/CustomCursor";
import SmartChatbot from "./components/chat/SmartChatbot";
import CookieConsent from "./components/common/CookieConsent";
import PageLoader from "./components/common/PageLoader";

import Home from "@/pages/Home";

// Lazy loaded routes
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyDetails = lazy(() => import("@/pages/properties/[id]"));
const PrivacyPolicy = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({ default: module.PrivacyPolicy }))
);
const LegalNotice = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({ default: module.LegalNotice }))
);
const TermsOfService = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({ default: module.TermsOfService }))
);
const NotFound = lazy(() => import("@/pages/NotFound"));
const Favorites = lazy(() => import("@/pages/Favorites"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={false}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
        <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
        <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
        <Route path="/favorites" element={<PageWrapper><Favorites /></PageWrapper>} />
        <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
        <Route path="/legal-notice" element={<PageWrapper><LegalNotice /></PageWrapper>} />
        <Route path="/terms-of-service" element={<PageWrapper><TermsOfService /></PageWrapper>} />

        <Route
          path="/properties"
          element={
            <ErrorBoundary fallback={<div className="py-40 text-center font-playfair italic text-stone-500">{t('errors.loading_properties', 'Error loading properties...')}</div>}>
              <PageWrapper><Properties /></PageWrapper>
            </ErrorBoundary>
          }
        />

        <Route
          path="/properties/:id"
          element={
            <ErrorBoundary fallback={<PropertyErrorFallback />}>
              <PageWrapper><PropertyDetails /></PageWrapper>
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  const { i18n } = useTranslation();

  const seoData = {
    title: i18n.language === "es"
      ? "DEM Properties | Servicios Inmobiliarios y Selección de Viviendas"
      : "DEM Properties | Real Estate Services & Property Selection",
    description: i18n.language === "es"
      ? "Asesoramiento inmobiliario profesional. Encuentra tu próxima vivienda en nuestra selección de propiedades."
      : "Professional real estate advice. Find your next home in our property selection."
  };

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <link rel="canonical" href="https://demproperties.es" />
          <meta property="og:title" content={seoData.title} />
          <meta property="og:description" content={seoData.description} />
        </Helmet>

        <ScrollToTop />
        <CustomCursor />
        <WhatsAppButton />
        <SmartChatbot />
        <CookieConsent />

        <main className="min-h-screen">
          <Suspense fallback={<PageLoader />}>
            <AnimatedRoutes />
          </Suspense>
        </main>

        <Footer />
      </BrowserRouter>
    </HelmetProvider>
  );
}
